import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Square, Video } from "lucide-react";

const MAX_DURATION_SEC = 60;
const TARGET_WIDTH = 1280;
const TARGET_HEIGHT = 720;

/** Overlay: square covers 70% of height, centered (15% top, 15% bottom outside) */
const OVERLAY_HEIGHT_PERCENT = 0.7;

interface Exterior360VideoCaptureViewLabels {
  startRecording?: string;
  stopRecording?: string;
  recording?: string;
  cameraStarting?: string;
  cameraError?: string;
  landscapeHint?: string;
  holdSteady?: string;
  maxDuration?: string;
}

interface Exterior360VideoCaptureViewProps {
  onRecordingComplete: (blob: Blob) => void;
  labels?: Exterior360VideoCaptureViewLabels;
}

export function Exterior360VideoCaptureView({
  onRecordingComplete,
  labels = {},
}: Exterior360VideoCaptureViewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const stopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isStarting, setIsStarting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamReady, setStreamReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);

  const startStream = useCallback(async () => {
    if (streamRef.current) return;
    setIsStarting(true);
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: TARGET_WIDTH, min: 640 },
          height: { ideal: TARGET_HEIGHT, min: 480 },
        },
        audio: false,
      });
      streamRef.current = stream;
      setStreamReady(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`${labels.cameraError ?? "Kunde inte starta kameran"}: ${msg}`);
    } finally {
      setIsStarting(false);
    }
  }, [labels.cameraError]);

  useEffect(() => {
    if (!streamReady || !streamRef.current || !videoRef.current) return;
    const video = videoRef.current;
    const stream = streamRef.current;
    video.srcObject = stream;
    video.play().catch(() => {});
  }, [streamReady]);

  const stopStream = useCallback(() => {
    if (stopTimeoutRef.current) {
      clearInterval(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStreamReady(false);
  }, []);

  useEffect(() => {
    return () => stopStream();
  }, [stopStream]);

  const containerRef = useRef<HTMLDivElement>(null);

  const enterFullscreenAndLockLandscape = useCallback(async () => {
    const el = containerRef.current;
    if (el) {
      try {
        await el.requestFullscreen();
      } catch {}
    }
    try {
      if (
        typeof screen !== "undefined" &&
        "orientation" in screen &&
        typeof (
          screen as { orientation?: { lock?: (mode: string) => Promise<void> } }
        ).orientation?.lock === "function"
      ) {
        await (
          screen as { orientation: { lock: (mode: string) => Promise<void> } }
        ).orientation.lock("landscape-primary");
      }
    } catch {}
  }, []);

  const exitFullscreenAndUnlock = useCallback(() => {
    try {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    } catch {}
    try {
      if (
        typeof screen !== "undefined" &&
        "orientation" in screen &&
        typeof (screen as { orientation?: { unlock?: () => void } }).orientation
          ?.unlock === "function"
      ) {
        (
          screen as { orientation: { unlock: () => void } }
        ).orientation.unlock();
      }
    } catch {}
  }, []);

  const isFullscreen = () => {
    const el = containerRef.current;
    if (!el) return false;
    const doc = document as Document & { webkitFullscreenElement?: Element };
    return (
      document.fullscreenElement === el || doc.webkitFullscreenElement === el
    );
  };

  const startRecording = useCallback(async () => {
    if (!streamRef.current || !videoRef.current) return;

    // First click: enter fullscreen, don't start recording yet
    if (!isFullscreen()) {
      await enterFullscreenAndLockLandscape();
      return; // User must click "Starta inspelning" again in fullscreen
    }

    chunksRef.current = [];
    setElapsedSec(0);

    try {
      const recorder = new MediaRecorder(streamRef.current, {
        mimeType: MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
          ? "video/webm;codecs=vp9"
          : "video/webm",
        videoBitsPerSecond: 2_500_000,
      });

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        if (chunksRef.current.length > 0) {
          const blob = new Blob(chunksRef.current, { type: "video/webm" });
          onRecordingComplete(blob);
        }
      };

      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      setIsRecording(true);

      const interval = setInterval(() => {
        setElapsedSec((s) => {
          const next = s + 1;
          if (
            next >= MAX_DURATION_SEC &&
            mediaRecorderRef.current?.state === "recording"
          ) {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current = null;
            setIsRecording(false);
            clearInterval(interval);
            exitFullscreenAndUnlock();
          }
          return next;
        });
      }, 1000);
      stopTimeoutRef.current = interval;
    } catch (e) {
      setError("Inspelning misslyckades");
    }
  }, [
    onRecordingComplete,
    enterFullscreenAndLockLandscape,
    exitFullscreenAndUnlock,
  ]);

  const stopRecording = useCallback(() => {
    if (stopTimeoutRef.current) {
      clearInterval(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === "recording") {
      recorder.stop();
      mediaRecorderRef.current = null;
      setIsRecording(false);
    }
    exitFullscreenAndUnlock();
  }, [exitFullscreenAndUnlock]);

  return (
    <div
      ref={containerRef}
      className="flex flex-col h-full [&:fullscreen]:min-h-[100dvh] [&:fullscreen]:min-w-[100dvw] [&:fullscreen]:h-[100dvh] [&:fullscreen]:w-[100dvw] [&:fullscreen]:max-h-none [&:fullscreen]:bg-black [&:fullscreen]:rounded-none [&:fullscreen]:p-2 [&:fullscreen]:box-border [&:fullscreen]:overflow-hidden [&:-webkit-full-screen]:overflow-hidden [&:-webkit-full-screen]:min-h-[100dvh] [&:-webkit-full-screen]:min-w-[100dvw] [&:-webkit-full-screen]:h-[100dvh] [&:-webkit-full-screen]:w-[100dvw] [&:-webkit-full-screen]:bg-black [&:fullscreen_.video-area]:max-h-none [&:fullscreen_.video-area]:flex-1 [&:fullscreen_.video-area]:min-h-0 [&:fullscreen_.video-area]:aspect-auto [&:fullscreen_.video-area]:rounded-none [&:fullscreen_.video-area]:ring-0 [&:fullscreen_.video-area]:ring-offset-0 [&:fullscreen_.video-area]:shadow-none [&:-webkit-full-screen_.video-area]:max-h-none [&:-webkit-full-screen_.video-area]:flex-1 [&:-webkit-full-screen_.video-area]:min-h-0 [&:-webkit-full-screen_.video-area]:aspect-auto"
    >
      <div
        className="video-area relative w-full flex-1 min-h-[200px] max-h-[50vh] sm:max-h-[45vh] bg-slate-900 rounded-2xl overflow-hidden ring-2 ring-slate-700/50 ring-offset-2 ring-offset-slate-100 dark:ring-offset-slate-900 shadow-xl shrink-0"
        style={{ aspectRatio: "16/9" }}
      >
        {!streamReady && !error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/95 z-10">
            {isStarting ? (
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-10 w-10 text-primary animate-spin" />
                <p className="text-sm text-slate-300">
                  {labels.cameraStarting ?? "Startar kamera…"}
                </p>
              </div>
            ) : (
              <Button
                onClick={startStream}
                size="lg"
                className="h-12 px-8 rounded-xl font-semibold"
              >
                {labels.startRecording ?? "Starta inspelning"}
              </Button>
            )}
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center p-6 bg-slate-900/95 z-10">
            <p className="text-sm text-red-300 text-center">{error}</p>
          </div>
        )}

        {streamReady && (
          <>
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectFit: "cover" }}
            />
            {/* Overlay: square guide – 70% of height, centered (15% top/bottom outside) */}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none border-2 border-white/80 rounded-lg shadow-lg"
              style={{
                height: `${OVERLAY_HEIGHT_PERCENT * 100}%`,
                width: `${OVERLAY_HEIGHT_PERCENT * 100}%`,
                minWidth: 0,
                minHeight: 0,
              }}
              aria-hidden
            />
            {isRecording && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-600/90 text-white text-sm font-medium">
                <span className="flex h-2 w-2 rounded-full bg-white animate-pulse" />
                {labels.recording ?? "Inspelning"}
                <span>
                  {elapsedSec}s / {MAX_DURATION_SEC}s
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {streamReady && (
        <div className="flex flex-col gap-2 mt-4 shrink-0">
          <p className="text-xs text-muted-foreground text-center">
            {labels.landscapeHint ??
              "Håll telefonen liggande. Håll hela bilen inom den vita rutan."}
          </p>
          {!isRecording ? (
            <Button
              onClick={startRecording}
              size="lg"
              className="w-full h-11 rounded-xl font-semibold"
            >
              <Video className="h-4 w-4 mr-2" />
              {labels.startRecording ?? "Starta inspelning"}
            </Button>
          ) : (
            <Button
              onClick={stopRecording}
              variant="destructive"
              size="lg"
              className="w-full h-11 rounded-xl font-semibold"
            >
              <Square className="h-4 w-4 mr-2 fill-current" />
              {labels.stopRecording ?? "Stoppa inspelning"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
